import { getAllDashboardRows } from '@/api/api';
import { getIsConfigLoaded } from '@/store/config/selectors';
import { ConfigActionTypes } from '@/store/config/types';
import { QueryKeys } from '@/types';
import { APPLICANT_REGISTRATION_DELAY, INTAKE_ALL_END_DATE, INTAKE_ALL_START_DATE } from '@/utilities/constants';
import isTesting from '@/utilities/isTesting';
import { isAgentOrAgencyRole, isPartnerRole } from '@/utilities/roles/permissions';
import { all, call, cancelled, delay, fork, getContext, put, select, take, takeLatest } from 'redux-saga/effects';
import { ApplyActionTypes } from '../../apply/types';
import { MATCH_FILTER_NAMES } from 'shared-components';
import { ApplicationStatus, FilterKey, IIntakeIdWithDates } from 'api-paths-cas';
import { logout } from '../../auth/actions';
import { AuthState } from '../../auth/types';
import { getAuth, getDefaultIntakeId, getIntakeId, getIntakeRange } from '../../selectors';
import * as actions from '../actions';
import { IGetAllDashboardRowsParams, offersDashboardActionTypes } from '../types';
import { getIdleParamsFromParams, getFilterKeysFromParams, getUniversityFromParams, toYearMonth } from './helpers';
import { TableRow } from '@/components/organisms/ApplyDataGrid/table/types';
import { DefaultDashboardTabIds } from 'apply-data';
export let getAllOffersAbortController: AbortController;

export const createGetAllDashboardRowsParams = ({
  auth,
  intakeId,
  intakeRange,
  idToken,
}: {
  auth: AuthState;
  intakeId: string;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}): IGetAllDashboardRowsParams | undefined => {
  const params = new URLSearchParams(window.location.search);

  const uni = getUniversityFromParams(auth, params);

  const isAgent = isAgentOrAgencyRole(auth.role);
  const isPartner = isPartnerRole(auth.role);

  const startDate = (isAgent || isPartner) && toYearMonth(intakeRange?.startDate ?? INTAKE_ALL_START_DATE);
  const endDate = (isAgent || isPartner) && toYearMonth(intakeRange?.endDate ?? INTAKE_ALL_END_DATE);

  // Saga [FilterKey] => getFilterKeysFromParams [ApiFilterKey] => api.ts Local Endpoint [ApplicationPGFilterMap | ApplicantOfferPGFilterMap]
  const filters = getFilterKeysFromParams(params);
  const { idleFrom, idleTo } = getIdleParamsFromParams(params);

  const agentId = auth.agencyId;
  const officeId = auth.currentUser?.agencyOffice;
  const currentUserRegionId = auth.currentUser?.regionAccessId;

  const defaultStages = {
    applicationStages: [ApplicationStatus.InProgress, ApplicationStatus.Completed],
    offerStages: [],
  };

  let appStages = params.getAll(QueryKeys.APPLICATION_STAGE);
  let offerStages = params.getAll(QueryKeys.OFFER_STAGE);
  const currentTab = params.getAll(QueryKeys.CURRENT_TAB);

  // When loading the dashboard for the first time, if no stages are specified due to missing query parameters,
  // we are on the leads tab and hence load the default application stages for the leads tab.
  const noApplicationStages = !appStages || appStages.length === 0;
  const noOfferStages = !offerStages || offerStages.length === 0;
  // Only assume default tag if the current tab query param is also missing or defaulted to LEADS
  const currentTabLeadOrNull =
    !currentTab || currentTab.length === 0 || currentTab.includes(DefaultDashboardTabIds.LEADS);

  if (currentTabLeadOrNull && noApplicationStages && noOfferStages) {
    appStages = defaultStages.applicationStages;
    offerStages = defaultStages.offerStages;
  }

  getAllOffersAbortController = new AbortController();
  return {
    abortController: getAllOffersAbortController,
    idToken,
    startDate,
    endDate,

    appStages,
    offerStages,

    intakeId,
    uni,
    page: Number.parseInt(params.get(QueryKeys.PAGE) || '1', 10),
    sortCol: params.get(QueryKeys.SORT_COL),
    sortDir: params.get(QueryKeys.SORT_DIR),
    search: params.get(QueryKeys.SEARCH),

    // Filters
    filters,

    // office:
    agentId,
    officeId,
    currentUserRegionId,

    // Programmable tags:
    tag: params.getAll(FilterKey.TAGS),
    programmableTag: params.getAll(FilterKey.PROGRAMMABLE_TAGS),
    tagsMatch: params.getAll(MATCH_FILTER_NAMES.TAGS_MATCH),
    programmableTagsMatch: params.getAll(MATCH_FILTER_NAMES.PROGRAMMABLE_TAGS_MATCH),

    idleFrom,
    idleTo,
  } as IGetAllDashboardRowsParams;
};

export function* handleGetAllDashboardRows(): Generator {
  const params = new URLSearchParams(window.location.search);
  let res: TableRow[];
  const auth: AuthState = yield select(getAuth);
  const intakeIdAuth: string = yield select(getIntakeId);
  const defaultIntake: string = yield select(getDefaultIntakeId);
  const intakeId = params.get('intakeId') || intakeIdAuth || defaultIntake;
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);

  if (!intakeId && !isTesting) {
    console.warn('handleGetAllLeads, intakeId not provided or in test mode.');
    return;
  }
  const context = yield getContext('sagaContext');
  const idToken: string = yield call(context.getIdToken);

  try {
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.warn('Refresh Token has Expired - handleGetAllDashboardRows');
      return;
    }

    const getAllRowsParams = createGetAllDashboardRowsParams({
      auth,
      intakeId,
      idToken,
      intakeRange,
    });

    if (!getAllRowsParams) {
      return;
    }

    getAllOffersAbortController = new AbortController();
    res = yield call(getAllDashboardRows, getAllRowsParams);
    yield put(actions.setSelectedRows([]));
  } catch (err) {
    console.error(err);
    yield put(actions.getDashboardRowsFailure(err));
    return;
  } finally {
    if (yield cancelled()) {
      // the task was cancelled
      getAllOffersAbortController.abort();
      return;
    }
  }
  yield put(actions.getDashboardRowsSuccess(res));
}

// New saga to ensure config is loaded before handling get all applications
function* ensureConfigAndThenGetAllApplications(_action: ReturnType<typeof actions.requestDashboardRows>) {
  const isConfigLoaded: boolean = yield select(getIsConfigLoaded);
  if (!isConfigLoaded) {
    yield take([ConfigActionTypes.GET_UNI_CONFIG_SUCCESS]);
  }
  yield* handleGetAllDashboardRows();
}

function* watchGetLeads() {
  // Now, takeLatest will call ensureConfigAndThenGetAllApplications.
  // This saga will handle the config check before calling handleGetAllApplications.
  yield takeLatest(offersDashboardActionTypes.GET_DASHBOARD_ROWS, ensureConfigAndThenGetAllApplications);
}

function* ensureConfigAndThenGetAllApplicationsWithDelay(action: ReturnType<typeof actions.requestDashboardRows>) {
  yield delay(APPLICANT_REGISTRATION_DELAY);
  yield call(ensureConfigAndThenGetAllApplications, action);
}

function* GetDashboardRowsSaga() {
  yield all([
    fork(watchGetLeads),
    takeLatest(ApplyActionTypes.AGENT_REGISTER_APPLICANT_SUCCESS, ensureConfigAndThenGetAllApplicationsWithDelay),
    takeLatest(ApplyActionTypes.UNI_REGISTER_APPLICANT_SUCCESS, ensureConfigAndThenGetAllApplicationsWithDelay),
  ]);
}

export default GetDashboardRowsSaga;
